import { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar
} from 'recharts';
import { TrendingUp, Eye, Users, Globe, FileText, ExternalLink } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white p-5 rounded-sm border border-stone-200" data-testid={`analytics-stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-center gap-3 mb-2">
      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${color}`}>
        <Icon size={15} className="text-white" />
      </div>
      <span className="text-xs text-stone-500 uppercase tracking-wider">{label}</span>
    </div>
    <p className="text-2xl font-serif text-stone-900">{value}</p>
    {sub && <p className="text-xs text-stone-400 mt-1">{sub}</p>}
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-stone-900 text-stone-50 px-3 py-2 rounded-sm text-xs shadow-lg">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {p.value}
        </p>
      ))}
    </div>
  );
};

const AnalyticsTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/analytics/overview?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="analytics-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-stone-800"></div>
      </div>
    );
  }

  const dailyStats = data?.daily_stats || [];
  const topPages = data?.top_pages || [];
  const topPosts = data?.top_posts || [];
  const topReferrers = data?.top_referrers || [];

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Period Selector */}
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-lg text-stone-900">Site Analytics</h2>
        <div className="flex gap-1 bg-white border border-stone-200 rounded-sm p-1" data-testid="analytics-period-selector">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-3 py-1.5 text-xs rounded-sm transition-colors ${
                days === d ? 'bg-stone-900 text-stone-50' : 'text-stone-600 hover:bg-stone-100'
              }`}
              data-testid={`period-${d}d`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={Eye} label="Total Views" value={data?.total_views || 0} sub={`Last ${days} days`} color="bg-stone-800" />
        <StatCard icon={Users} label="Unique Visitors" value={data?.unique_visitors || 0} sub={`Last ${days} days`} color="bg-amber-700" />
        <StatCard icon={FileText} label="Top Pages" value={topPages.length} color="bg-emerald-700" />
        <StatCard icon={Globe} label="Referrers" value={topReferrers.length} color="bg-sky-700" />
      </div>

      {/* Views Chart */}
      <div className="bg-white p-6 rounded-sm border border-stone-200" data-testid="analytics-chart">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={16} className="text-stone-500" />
          <h3 className="text-sm font-medium text-stone-700">Views & Visitors</h3>
        </div>
        {dailyStats.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-stone-400 text-sm">
            No data yet. Views will appear as visitors browse your site.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={dailyStats} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="viewsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#292524" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#292524" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="visitorsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#b45309" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#b45309" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e7e5e4" />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#a8a29e' }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11, fill: '#a8a29e' }} allowDecimals={false} />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="views" name="Views" stroke="#292524" fill="url(#viewsFill)" strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" name="Visitors" stroke="#b45309" fill="url(#visitorsFill)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Bottom Grid: Top Pages, Posts, Referrers */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Top Pages */}
        <div className="bg-white p-5 rounded-sm border border-stone-200" data-testid="top-pages-list">
          <h3 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
            <Globe size={14} /> Top Pages
          </h3>
          {topPages.length === 0 ? (
            <p className="text-xs text-stone-400 py-4 text-center">No page data yet</p>
          ) : (
            <div className="space-y-2">
              {topPages.slice(0, 8).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-stone-700 truncate flex-1 mr-2">{p.page}</span>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-stone-500">{p.visitors} visitors</span>
                    <span className="font-medium text-stone-900">{p.views}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Posts */}
        <div className="bg-white p-5 rounded-sm border border-stone-200" data-testid="top-posts-list">
          <h3 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
            <FileText size={14} /> Top Posts
          </h3>
          {topPosts.length === 0 ? (
            <p className="text-xs text-stone-400 py-4 text-center">No post views yet</p>
          ) : (
            <div className="space-y-2">
              {topPosts.slice(0, 8).map((p, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-stone-700 truncate flex-1 mr-2">{p.slug}</span>
                  <span className="font-medium text-stone-900">{p.views}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top Referrers */}
        <div className="bg-white p-5 rounded-sm border border-stone-200" data-testid="top-referrers-list">
          <h3 className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
            <ExternalLink size={14} /> Top Referrers
          </h3>
          {topReferrers.length === 0 ? (
            <p className="text-xs text-stone-400 py-4 text-center">No referrer data yet</p>
          ) : (
            <div className="space-y-2">
              {topReferrers.slice(0, 8).map((r, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="text-stone-700 truncate flex-1 mr-2">{r.referrer}</span>
                  <span className="font-medium text-stone-900">{r.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsTab;
